import React, { useEffect, useRef, useState } from "react";
import "./aboutPage.css";

// ─── DATA ────────────────────────────────────────────────────────────────────
// Updated: All data focused on Arklon Lap Spools

const STATS_DATA = [
  { id: 1, num: "1997", label: "Arklon® Est." },
  { id: 2, num: "300K+", label: "Lap Spools / Year" },
  { id: 3, num: "29+", label: "Years of Arklon® Trust" },
  // { id: 4, num: "ISO", label: "Arklon® Certified" },
];

const PILLARS_DATA = [
  {
    id: 1,
    index: "01",
    title: "Local Expertise",
    text: "Leverage Arklon's® deep industry knowledge, personal manufacturing connections and decades of hands-on experience for unmatched lap spool quality.",
  },
  {
    id: 2,
    index: "02",
    title: "Precision Engineering",
    text: "Every Arklon® lap spool is manufactured to exact tolerances — because the textile industries we serve leave no room for error.",
  },
  {
    id: 3,
    index: "03",
    title: "Tailored Solutions",
    text: "We design custom Arklon® lap spools exclusively matched to your application requirements — precise, private, and built to last.",
  },
];

const TEAM_DATA = [
  {
    id: 1,
    name: "Krishna Praveen",
    role: "CEO of Emmark Industries",
    description:
      "Leads Emmark Industries with a strong strategic vision and market-focused approach, ensuring the Arklon® brand and customer promise remain closely aligned with manufacturing excellence and evolving industry needs.",
    img: "/assets/Krishna.png",
  },
  {
    id: 2,
    name: "S Madhava Prajith",
    role: "Director of Production",
    description:
      "Brings 29 years of production expertise to Emmark Industries, overseeing plant operations with a strong focus on efficiency, precision, and quality excellence.",
    img: "/assets/madhav.png",
  },
  {
    id: 3,
    name: "Rama Prasad",
    role: "Director, Administration & Finance",
    description:
      "Brings 29 years of industry expertise to Emmark Industries, leading the company’s financial and administrative operations with a focus on stability, efficiency, and sustainable growth.",
    img: "/assets/rama.png",
  },
];

// All images focused on lap spools
const GALLERY_IMGS = [
  "/assets/marzoli spool3.png",
  "/assets/LH -10 Lap spool3.png",
  "/assets/Cherry hara Sl3.png",
  "/assets/Trutzschler  T-10 lap spool3.png",
  "/assets/Vouk Rd-3002.jpeg",
  "/assets/E-36 Omega lap spool3.png",
];

const CLIENTS = ["Tata Steel", "Jindal Steel", "SAIL", "JSW Steel"];

// Helper function to add trademark symbol to Arklon
const addTrademarkToArklon = (text) => {
  // Replace Arklon with Arklon® (case sensitive)
  return text.replace(/Arklon/g, 'Arklon®');
};

// ─── HOOKS ───────────────────────────────────────────────────────────────────

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView];
}

// ─── COMPONENT ───────────────────────────────────────────────────────────────

export default function AboutPage() {
  const [heroRef, heroInView] = useInView(0.05);
  const [introRef, introInView] = useInView();
  const [pillarsRef, pillarsInView] = useInView();
  const [founderRef, founderInView] = useInView();
  const [teamRef, teamInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  return (
    <div className="ab-root">
      {/* ── 1. HERO ── */}
      <section className="ab-hero" ref={heroRef}>
        <div className="ab-hero__bg" aria-hidden="true" />
        <div className="ab-hero__overlay" aria-hidden="true" />

        {/* Marquee strip - Arklon focused */}
        <div className="ab-hero__marquee-wrap" aria-hidden="true">
          <div className="ab-hero__marquee-track">
            {Array(6)
              .fill("Arklon® Lap Spools · Precision Engineering · Since 1997 · ")
              .map((t, i) => (
                <span key={i} className="ab-hero__marquee-item">
                  {t}
                </span>
              ))}
          </div>
        </div>

        <div className="ab-container">
          <div
            className={`ab-hero__content${heroInView ? " ab--visible" : ""}`}
          >
            {/* <p className="ab-eyebrow">
              <span className="arklon-highlight">Arklon®</span> — About us
            </p> */}
            <h1 className="ab-hero__title">
              <span className="ab-hero__title-serif">Arklon®</span>
              <span className="ab-hero__title-outline"> Emmark Industries - The Largest Lap Spools Manufacturers</span>
              {/* <span className="ab-hero__title-serif">Excellence</span> */}
            </h1>
          </div>
        </div>

        {/* Stats strip - Arklon branded */}
        <div className="ab-hero__stats">
          {STATS_DATA.map((s) => (
            <div key={s.id} className="ab-hero__stat">
              <span className="ab-hero__stat-num">{s.num}</span>
              <span className="ab-hero__stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── 2. INTRO ── */}
      <section className="ab-intro" ref={introRef}>
        <div className="ab-container ab-intro__grid">
          {/* Left — editorial label */}
          <div
            className={`ab-intro__label-col${introInView ? " ab--slide-up" : ""}`}
          >
            <span className="ab-eyebrow">
              <span className="arklon-highlight">Arklon®</span> Story
            </span>
            <p className="ab-intro__aside">
              Crafted by Arklon® engineers,
              <br />
              for textile industries that
              <br />
              demand perfect lap spools.
            </p>
          </div>

          {/* Right — body copy */}
          <div
            className={`ab-intro__copy-col${introInView ? " ab--slide-up ab--delay-1" : ""}`}
          >
            <h2 className="ab-section-heading">
              Exclusive <span className="arklon-highlight">Arklon®</span> Lap
              Spools Manufacturers.
            </h2>
            <p className="ab-body-text">
              Established in 1997,{" "}
              <strong className="arklon-highlight">Emmark Industries</strong>{" "}
              has grown to become one of India's most trusted manufacturers of
              precision-engineered lap spools and textile spinning components —
              headquartered in Palakkad, Kerala.
            </p>
            <p className="ab-body-text">
              We specialise in{" "}
              <strong className="arklon-highlight">Arklon®</strong>
              lap spools that meet the
              stringent demands of the textile
              industries. Our state-of-the-art facility, equipped with advanced
              CNC machinery and an in-house testing laboratory, delivers{" "}
              <strong className="arklon-highlight">Arklon®</strong> lap spools
              that exemplify precision, durability, and uncompromising quality.
            </p>
            <p className="ab-body-text">
              Our commitment to{" "}
              <strong className="arklon-highlight">Arklon®</strong> excellence
              has earned us partnerships with industry giants —  <strong className="arklon-highlight"> LMW </strong>, and numerous other Fortune 500 companies trust{" "}
              <strong className="arklon-highlight">Arklon®</strong> lap spools on
              their floors.
            </p>
          </div>
        </div>
      </section>

      {/* ── 3. DIVIDER IMAGE with Arklon lap spool ── */}
      <section className="ab-cinematic">
        <div className="ab-cinematic__img-wrap">
          <img
            src="/assets/bn1.png"
            alt="Emmark Industries lap spool manufacturing showcase"
            className="ab-cinematic__img"
          />
          <div className="ab-cinematic__badge">
            <span className="ab-cinematic__badge-num">
              29<sup>+</sup>
            </span>
            <span className="ab-cinematic__badge-txt">
              Years of Arklon® Lap Spool Trust
            </span>
          </div>
        </div>
      </section>

      {/* ── 4. THREE PILLARS (Arklon focused) ── */}
      <section className="ab-pillars" ref={pillarsRef}>
        <div className="ab-container">
          <p className="ab-eyebrow ab-eyebrow--centered">
            What Sets <span className="arklon-highlight">Arklon®</span> Apart
          </p>
          <h2 className="ab-section-heading ab-section-heading--centered">
            An immersive lap spool partnership built
            <br className="ab-br" />
            on Arklon® expertise &amp; precision
          </h2>

          <div className="ab-pillars__grid">
            {PILLARS_DATA.map((p, i) => (
              <div
                key={p.id}
                className={`ab-pillar${pillarsInView ? ` ab--slide-up ab--delay-${i}` : ""}`}
              >
                <span className="ab-pillar__index">{p.index}</span>
                <h3 className="ab-pillar__title">
                  <span className="arklon-highlight">Arklon®</span>{" "}
                  {p.title}
                </h3>
                <p className="ab-pillar__text">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. FOUNDER (Editorial Redesign) ── */}
      <section className="ab-founder-v2" ref={founderRef}>
        <div className="ab-container-v2 ab-founder-v2__layout">

          {/* Visual Column */}
          <div className={`ab-founder-v2__frame-side${founderInView ? " ab--slide-up" : ""}`}>
            <div className="ab-founder-v2__image-wrapper">
              <img
                src="assets/Founder.webp"
                alt="K V Sasidharan — Founder, Emmark Industries"
                className="ab-founder-v2__img"
              />
              {/* Overlapping Floating Badge */}
              <div className="ab-founder-v2__floating-badge">
                <span className="ab-founder-v2__badge-years">29+</span>
                <span className="ab-founder-v2__badge-label">Years of Arklon® Precision</span>
              </div>
            </div>
          </div>

          {/* Content Column */}
          <div className={`ab-founder-v2__content-side${founderInView ? " ab--slide-up ab--delay-1" : ""}`}>
            <span className="ab-v2-eyebrow">Leadership Perspective</span>

            <h2 className="ab-v2-heading">
              The Engineering <br />
              of <span className="arklon-highlight">Enduring Trust</span>
            </h2>

            <div className="ab-founder-v2__narrative">
              <p className="ab-founder-v2__lead-text">
                "In 1997, Emmark Industries was born out of a singular, uncompromising objective: to manufacture lap spools capable of redefining spinning mill efficiencies."
              </p>

              <p className="ab-founder-v2__body-text">
                Nearly three decades later, that vision has materialized into the <strong className="arklon-highlight">Arklon®</strong> standard. We have never viewed lap spools as mere components; we engineer them as critical links in your production value chain. Our legacy is anchored in precision, and our future belongs to sustainable innovation.
              </p>
            </div>

            <div className="ab-founder-v2__signature-block">
              <div className="ab-founder-v2__sig-line" />
              <div className="ab-founder-v2__author-info">
                <h4 className="ab-founder-v2__name">K V Sasidharan</h4>
                <p className="ab-founder-v2__title">Founder, Emmark Industries</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ── 7. VISION & MISSION (Arklon focused) ── */}
      <section className="ab-vm">
        <div className="ab-container ab-vm__grid">
          <div className="ab-vm__card">
            <span className="ab-vm__icon" aria-hidden="true">
              ◎
            </span>
            <h3 className="ab-vm__title">
              <span className="arklon-highlight">Arklon®</span> Vision
            </h3>
            <p className="ab-vm__text">
              To be the most trusted and preferred manufacturer of precision lap
              spools in the World and be recognised for Arklon's® commitment to quality,
              innovation, and sustainable manufacturing process.
            </p>
          </div>
          <div className="ab-vm__divider" aria-hidden="true" />
          <div className="ab-vm__card">
            <span className="ab-vm__icon" aria-hidden="true">
              ◉
            </span>
            <h3 className="ab-vm__title">
              <span className="arklon-highlight">Arklon®</span> Mission
            </h3>
            <p className="ab-vm__text">
              To deliver world-class Arklon® lap spools through continuous
              improvement, technological advancement, and a dedicated team
              committed to exceeding customer expectations while maintaining the
              highest ethical standards.
            </p>
          </div>
        </div>
      </section>

      {/* ── 8. TEAM (Arklon branded) ── */}
      <section className="ab-team" ref={teamRef}>
        <div className="ab-container">
          <p className="ab-eyebrow ab-eyebrow--centered">
            Meet <span className="arklon-highlight">Arklon®</span> Experts
          </p>
          <h2 className="ab-section-heading ab-section-heading--centered">
            Our Arklon® Leadership Team
          </h2>
          <p className="ab-team__sub">
            The driving force behind{" "}
            <span className="arklon-highlight">Emmark Industries</span> lap
            spool success and innovation.
          </p>

          <div className="ab-team__grid">
            {TEAM_DATA.map((m, i) => (
              <article
                key={m.id}
                className={`ab-team-card${teamInView ? ` ab--slide-up ab--delay-${i}` : ""}`}
              >
                <div className="ab-team-card__img-wrap">
                  <img src={m.img} alt={m.name} className="ab-team-card__img" />
                </div>
                <div className="ab-team-card__info">
                  <h4 className="ab-team-card__name">{m.name}</h4>
                  <p className="ab-team-card__role">{m.role}</p>
                  <p className="ab-team-card__desc">{m.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── 9. CTA (Arklon focused) ── */}
      <section className="ab-cta" ref={ctaRef}>
        <div
          className={`ab-container ab-cta__inner${ctaInView ? " ab--slide-up" : ""}`}
        >
          <p className="ab-eyebrow ab-eyebrow--light">
            Start Your <span className="arklon-highlight-light">Arklon®</span>{" "}
            Journey
          </p>
          <h2 className="ab-cta__heading">
            Let's Build an Exceptional
            <br />
            Experience Together
          </h2>
          <div className="ab-cta__btns">
            <a href="/contact" className="ab-btn ab-btn--primary">
              Get Arklon® Quote
            </a>
            <a href="/products" className="ab-btn ab-btn--outline">
              Explore Arklon® Lap Spools
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}